import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core'
import { Subject, debounceTime, takeUntil } from 'rxjs'

import { ApiInvoiceService } from '@modules/private/invoices/data/services/api/api-invoice.service'
import { ApiPaymentService } from '@modules/private/payment/data/services/api/api-payment.service'
import { PaymentDateTarget } from '../payment-date-dialog/payment-date-dialog.component'
import { CONCEPT_LABEL } from '@modules/private/invoices/data/constants/conceptos'
import { DataTableService } from '@data/services/utils/data-table.service'

/** Una fila de la tabla, ya resuelta: nada de leer `a?.b?.c` desde la plantilla. */
export interface FilaPago {
  _id: string
  fecha: string | null
  clienteId: string | null
  cliente: string
  claveRastreo: string
  monto: number
  /** Lo que dice el LIBRO DE APLICACIONES, no el campo guardado del pago. */
  aplicado: number
  sinAplicar: number
  /** Desfase entre lo guardado y el libro. Distinto de cero = este pago necesita que alguien lo mire. */
  desfase: number
  estado: 'aplicado' | 'parcial' | 'sinAplicar' | 'atorado'
  /** Lo que se carga al desplegar la fila. `null` mientras no se haya pedido. */
  detalle: { aplicaciones: any[]; facturas: any[] } | null
  cargandoDetalle: boolean
  /** F-045 · El contexto no llegó: sin esto la sección diría «no se ha aplicado», que puede ser falso. */
  detailFailed?: boolean
}

/**
 * Lo que la pantalla necesita para abrir «Cancelar aplicación» sobre UNA aplicación del pago (F-045).
 *
 * Sale de la fila desplegada —`GET /payments/:id/context`— y no del libro mayor del arrendamiento: desde
 * Dinero no hay libro cargado, y el diálogo solo pide el pago y la aplicación para previsualizar.
 */
export interface PaymentApplicationCancelRequest {
  paymentId: string
  trackingKey: string
  applicationId: string
  letters: number[]
  amount: number
}

/**
 * A partir de cuántos días un pago sin repartir **deja de ser normal y pasa a ser un pendiente**.
 *
 * Sale de la D-07 del rediseño: tres días. Vivía en la casilla «Por aplicar» de una franja que la
 * D-02 sustituyó por el embudo del mes, y con ella se quedó sin sitio donde enseñarse; ahora vive en
 * la fila, que es donde se decide qué hacer con ese pago. Si algún día hay que moverlo, su casa es
 * Configuración de Cartera —donde ya viven la ventana de duplicados y la de calificación—, no otra
 * constante suelta.
 */
const DIAS_PARA_ALARMA = 3

/**
 * Los estados del PAGO en los que todavía queda dinero por repartir. Se filtra por `status` y no por
 * `pendingToApply` a propósito: un pago **atorado** tiene ese campo en cero y el libro dice que le
 * sobran cuarenta mil, así que filtrar por el campo dejaría fuera justo los que hay que mirar.
 *
 * ⚠️ **Lo que esta lente NO alcanza**: un pago marcado `APPLIED` cuyo LIBRO dice que no se aplicó
 * nada —el desfase que la fila marca con el triángulo ámbar—. Tiene dinero suelto y aun así queda
 * fuera, porque su `status` miente y el backend no sabe filtrar por el libro: `pendingLedger` se
 * calcula después de la consulta, sobre la página. Cerrarlo de verdad pide un filtro nuevo en la API
 * que cruce las aplicaciones vivas contra el monto; mientras tanto esos pagos siguen visibles en el
 * listado completo, con su aviso. No se disimula aquí porque el número de la ficha sería falso.
 */
const ESTADOS_PENDIENTES = ['PENDING', 'PARTIAL']

/** Cómo se lee cada estado, y de qué color va. */
const ESTADOS = {
  aplicado: { label: 'Aplicado', tono: 'ok' },
  parcial: { label: 'Parcial', tono: 'warn' },
  sinAplicar: { label: 'Sin aplicar', tono: 'info' },
  atorado: { label: 'Atorado', tono: 'bad' },
} as const

/**
 * La tabla de Dinero: los pagos, con lo esencial arriba y el detalle al desplegar.
 *
 * ## Por qué no usa la tabla genérica
 *
 * `app-main-table` sirve para listar entidades con filtro por columna, y aquí eso **no funcionaba**:
 * cinco cuadros de filtro descuadrados sobre las cabeceras, de los que varios ni siquiera filtraban
 * porque su campo no estaba declarado en el backend —`client.name` es el caso: se pintaba el cuadro y
 * la consulta lo ignoraba en silencio—. La pregunta real de Operaciones es una sola: «encuéntrame
 * este pago», por cliente o por clave. Un cuadro.
 *
 * La genérica **no se toca**: la usan muchas pantallas y romperla ahí saldría en sitios que nadie
 * miró. Esta vive aparte.
 *
 * ## Por qué las filas se despliegan
 *
 * El detalle era un modal, y un modal tapa la lista de la que salió: para comparar dos pagos había
 * que abrir, leer, cerrar y volver a abrir. Desplegando, el detalle sale **debajo de su fila** y la
 * lista sigue ahí — el patrón que Exigibilidad ya usa para las letras.
 *
 * Y se pide **al desplegar**, no al cargar la página: el detalle son dos consultas por pago y nadie
 * abre los diez.
 *
 * ## Por qué «Aplicado» viene del libro
 *
 * `amount - pendingToApply` es la cuenta obvia y **puede mentir**: `pendingToApply` es una copia
 * guardada que se desfasa. Hay un pago en dev con `pendingToApply: 0` cuyo libro dice que solo se le
 * aplicaron $20,000 de $60,000; esa cuenta enseñaría $60,000 aplicados que no existen. El backend
 * manda `appliedLedger` derivado del libro, y el desfase viaja aparte para poder señalarlo.
 */
@Component({
  selector: 'app-payments-table',
  templateUrl: './payments-table.component.html',
  styleUrls: ['./payments-table.component.scss'],
})
export class PaymentsTableComponent implements OnInit, OnDestroy {
  /** Abrir el modal de ajuste sobre un pago. Lo resuelve la pantalla, que es quien tiene el modal. */
  /**
   * Anular una carga repetida. **Es lo único que la fila puede hacer con el pago**, y por eso el
   * botón se llama así y no «Ajustar».
   *
   * Se llamaba «Ajustar» con una llave inglesa —heredado al portar la tabla— y prometía una familia
   * de ajustes: destrabar, cerrar remanente, deshacer. El modal que abre desde aquí ofrece **una**
   * operación, y es destructiva. Un botón que promete más de lo que hace es peor que uno feo.
   */
  @Output() anular = new EventEmitter<FilaPago>()

  /** Aplicar el dinero que le queda al pago. El diálogo vive en la pantalla, como el de anular. */
  @Output() aplicar = new EventEmitter<FilaPago>()

  /** Registrar un pago a mano. El formulario vive en la pantalla, con su diálogo. */
  @Output() registrar = new EventEmitter<void>()

  /**
   * Cancelar UNA aplicación del pago, desde su renglón en «A dónde fue el dinero» (F-045).
   *
   * Es el camino para deshacer un pago ya repartido: «Anular» no cancela aplicaciones y el motor lo
   * bloquea con `HAS_APPLICATIONS`. El diálogo es el mismo de Exigibilidad y vive en la pantalla.
   */
  @Output() cancelApplication = new EventEmitter<PaymentApplicationCancelRequest>()

  /** Corregir el monto o la clave de rastreo del pago. El diálogo vive en la pantalla (F-045). */
  @Output() edit = new EventEmitter<FilaPago>()

  exportando = false

  /** Pago cuya fecha se está corrigiendo. */
  fechaPago: PaymentDateTarget | null = null
  fechaVisible = false

  filas: FilaPago[] = []
  total = 0
  cargando = true

  pagina = 0
  //* 30 · 60 · 90: los tamaños con los que Operaciones revisa un corte. El default es el más chico
  //* porque la tabla se lee de arriba abajo y treinta filas ya llenan su ventana.
  porPagina = 30
  readonly opcionesPagina = [30, 60, 90]

  busqueda = ''
  /**
   * F-045 · La búsqueda de las filas que SE VEN. `busqueda` cambia con cada tecla y las filas solo cuando
   * contesta la consulta (350 ms después, o más). Con el contador y el vacío leyendo lo tecleado, las filas
   * seguían siendo las de la búsqueda anterior hasta que contestaba la consulta: «Editar» abría el pago
   * equivocado bajo un contador que ya decía el nuevo.
   */
  shownSearch = ''
  /** La última consulta pedida: una respuesta vieja que llega tarde no pisa a la nueva. */
  private latestLoad = 0
  /** Lo mismo para el número de la lente: un conteo viejo que llega tarde no pisa al nuevo. */
  private latestCount = 0
  /** La lente de Operaciones: solo lo que todavía tiene dinero sin repartir. */
  soloPendientes = false
  /** Cuántos hay en TODA la cartera, no en esta página. `null` mientras no se sabe. */
  pendientesTotal: number | null = null
  /** Qué filas están desplegadas, por id. */
  abiertas = new Set<string>()

  readonly estados: Record<string, { label: string; tono: string }> = ESTADOS
  readonly esqueleto = [1, 2, 3, 4, 5, 6, 7, 8]

  private readonly buscar$ = new Subject<string>()
  private readonly destroy$ = new Subject<void>()

  constructor(
    private readonly api: ApiPaymentService,
    private readonly apiInvoice: ApiInvoiceService,
    private readonly dataTable: DataTableService,
  ) {}

  ngOnInit(): void {
    //* Se espera a que deje de teclear: sin esto son seis consultas para escribir «ARNOLD», y la
    //* última en contestar puede no ser la última que se pidió.
    //! Sin `distinctUntilChanged`, a propósito: recuerda lo último que emitió el debounce, no lo que se
    //! cargó por última vez. La lente y `recargar()` también cargan, así que con «ABC» buscado, teclear «D»,
    //! pulsar la lente y borrar la «D» antes de 350 ms dejaba fuera la vuelta a «ABC»: la tabla se quedaba
    //! en esqueleto sin consulta en curso. Repetir una consulta cuesta poco; `latestLoad` ya descarta las respuestas superadas.
    this.buscar$.pipe(debounceTime(350), takeUntil(this.destroy$)).subscribe(() => {
      this.pagina = 0
      this.cargar()
    })

    this.cargar()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  onBuscar(valor: string): void {
    this.busqueda = valor
    this.buscar$.next(valor)
  }

  /** Mientras la búsqueda escrita no es la de las filas, la tabla enseña el esqueleto y no ofrece acciones. */
  get isSearchStale(): boolean {
    return this.busqueda.trim() !== this.shownSearch
  }

  limpiarBusqueda(): void {
    if (!this.busqueda) return
    this.onBuscar('')
  }

  /**
   * Enciende o apaga la lente. Al encenderla se vuelve a la primera página: dejar la nueve puesta
   * sobre un listado que ahora tiene tres filas enseña una tabla vacía sin explicar por qué.
   */
  alternarPendientes(): void {
    this.soloPendientes = !this.soloPendientes
    this.pagina = 0
    this.cargar()
  }

  /**
   * Cuántos días lleva este pago con dinero sin repartir, **solo si ya pasó del umbral**. Devuelve
   * `null` cuando no hay nada que reclamar: sin saldo, sin fecha, o dentro de los días normales.
   *
   * La cuenta va contra la fecha del CLIENTE (cuándo pagó), no contra cuándo se capturó: el reclamo
   * es «su dinero lleva doce días aquí sin aplicarse», y eso empieza el día que pagó.
   */
  diasEsperando(fila: FilaPago): number | null {
    if (fila.sinAplicar <= 0.011 || !fila.fecha) return null
    const pago = new Date(fila.fecha)
    if (isNaN(pago.getTime())) return null

    //! Las dos fechas NO se leen igual, y es a propósito. La del pago es un DÍA de calendario
    //! guardado a medianoche UTC —así la pinta toda esta tabla (`date:'dd/MM/yyyy':'UTC'`)—,
    //! mientras que «hoy» es un instante y hay que aterrizarlo al día del operador: CDMX. Leer las
    //! dos en UTC hace que a partir de las 18:00 la ficha diga un día de más.
    const diaUTC = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())
    const [a, m, dd] = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date()).split('-').map(Number)
    const dias = Math.floor((Date.UTC(a, m - 1, dd) - diaUTC(pago)) / 86400000)

    return dias >= DIAS_PARA_ALARMA ? dias : null
  }

  //* `PaginatorState` de PrimeNG declara `first`/`rows` como opcionales, así que se leen con respaldo
  //* en vez de asumirlos: sin eso, un evento sin `rows` dividiría entre `undefined`.
  onPagina(e: { first?: number; rows?: number }): void {
    this.porPagina = e.rows ?? this.porPagina
    this.pagina = this.porPagina ? Math.floor((e.first ?? 0) / this.porPagina) : 0
    this.cargar()
  }

  /** Recarga sin perder la búsqueda ni la página. La usa la pantalla tras un ajuste. */
  abrirFecha(fila: FilaPago): void {
    this.fechaPago = { _id: fila._id, fecha: fila.fecha, monto: fila.monto, trackingKey: fila.claveRastreo, cliente: fila.cliente }
    this.fechaVisible = true
  }

  recargar(): void {
    this.cargar()
  }

  /**
   * Baja a Excel lo que la búsqueda tiene delante, no la página visible.
   *
   * Contabilidad cuadra el mes con este archivo, así que sale con las cifras del LIBRO —las mismas
   * que la pantalla— y no con `amount - pendingToApply`, que es la cuenta que puede mentir. El
   * límite es alto pero finito: un `limit` sin techo con un filtro de fecha empuja al listado a la
   * ruta «precisa», que corta a 15 000 y devuelve el total etiquetado como exacto (B-11).
   */
  exportar(): void {
    this.exportando = true
    const filtros: Record<string, unknown> = {}
    if (this.busqueda.trim()) filtros['q'] = { value: this.busqueda.trim(), matchMode: 'contains' }

    this.api.getAll(1, 10000, filtros).subscribe((r: any) => {
      this.exportando = false
      if (r.error) return

      const filas: FilaPago[] = (r.data?.data ?? []).map((p: any) => this.aFila(p))
      this.dataTable.exportExcel(
        filas.map((f: FilaPago) => ({
          'Fecha de pago': f.fecha ? new Date(f.fecha).toLocaleDateString('es-MX', { timeZone: 'UTC' }) : '',
          Cliente: f.cliente,
          'Clave de rastreo': f.claveRastreo,
          Monto: f.monto,
          Aplicado: f.aplicado,
          'Sin aplicar': f.sinAplicar,
          Estado: ESTADOS[f.estado].label,
          //* El desfase viaja al archivo: es lo que hace que un cuadre no cuadre, y esconderlo
          //* obliga a descubrirlo restando a mano.
          'Desfase contra el libro': f.desfase || 0,
        })),
      )
    })
  }

  estaAbierta(fila: FilaPago): boolean {
    return this.abiertas.has(fila._id)
  }

  /**
   * Despliega una fila y pide su detalle si todavía no lo tiene **o si el contexto no llegó** la vez
   * anterior.
   *
   * Un detalle que llegó se conserva al cerrar y volver a abrir: toda acción de la pantalla que cambia el
   * pago (ajuste, aplicar, cancelar aplicación, editar, fecha) recarga la tabla entera, y con ella el detalle. Uno cuyo contexto falló se vuelve a pedir
   * entero —las DOS mitades, comprobantes incluidos—, porque el aviso le dice a la persona que cierre y
   * despliegue otra vez.
   */
  alternar(fila: FilaPago): void {
    if (this.abiertas.has(fila._id)) {
      this.abiertas.delete(fila._id)
      return
    }

    this.abiertas.add(fila._id)
    if ((fila.detalle && !fila.detailFailed) || fila.cargandoDetalle) return

    fila.cargandoDetalle = true
    let pendientes = 2
    const listo = () => {
      if (--pendientes === 0) fila.cargandoDetalle = false
    }

    this.api.getPaymentContext(fila._id).subscribe((r) => {
      fila.detailFailed = !!r.error
      fila.detalle = { aplicaciones: r.error ? [] : (r.data?.aplicaciones ?? []), facturas: fila.detalle?.facturas ?? [] }
      listo()
    })
    this.apiInvoice.getByPayment(fila._id).subscribe((r) => {
      fila.detalle = { aplicaciones: fila.detalle?.aplicaciones ?? [], facturas: r.error ? [] : (r.data ?? []) }
      listo()
    })
  }

  /** El concepto de la factura en español. Es el mismo diccionario que usa la tabla de Comprobantes. */
  conceptoLabel(concepto: string): string {
    return CONCEPT_LABEL[concepto] || concepto || '—'
  }

  /**
   * A qué fue el dinero dentro del arrendamiento, en palabras.
   *
   * Un enganche y un residual **no tienen letra**, y un guion ahí hace pensar que el dato se perdió.
   */
  destino(app: any): string {
    if (app?.applicationType === 'FIRST_PAYMENT') return 'Pago inicial'
    if (app?.applicationType === 'RESIDUAL_PAYMENT') return 'Residual'
    const letras = app?.letras ?? []
    if (!letras.length) return '—'

    return letras.length === 1 ? `Letra ${letras[0]}` : `Letras ${letras.join(', ')}`
  }

  private cargar(): void {
    this.cargando = true
    this.contarPendientes()
    const request = ++this.latestLoad
    const search = this.busqueda.trim()

    this.api.getAll(this.pagina + 1, this.porPagina, this.filtros()).subscribe((r: any) => {
      if (request !== this.latestLoad) return
      this.cargando = false
      this.shownSearch = search
      if (r.error) {
        this.filas = []
        this.total = 0
        return
      }
      this.total = r.data?.totalItems ?? 0
      this.filas = (r.data?.data ?? []).map((p: any) => this.aFila(p))
      //* Al cambiar de página las filas son otras: dejar ids abiertos de la anterior haría que se
      //* desplegara una fila que el usuario nunca abrió si por casualidad comparten id.
      this.abiertas.clear()
    })
  }

  /** Lo que se le pide al backend: la búsqueda y, si está encendida, la lente. */
  private filtros(): Record<string, unknown> {
    const filtros: Record<string, unknown> = {}
    if (this.busqueda.trim()) filtros['q'] = { value: this.busqueda.trim(), matchMode: 'contains' }
    if (this.soloPendientes) filtros['status'] = { value: ESTADOS_PENDIENTES, matchMode: 'in' }

    return filtros
  }

  /**
   * El número que va en la lente. Se pide aparte, con `limit: 1`, porque tiene que contar TODA la
   * cartera y no la página que se está viendo — que es justo lo que haría contar las filas cargadas.
   * Respeta la búsqueda: si hay un término escrito, cuenta los pendientes DE esa búsqueda.
   */
  private contarPendientes(): void {
    const filtros: Record<string, unknown> = { status: { value: ESTADOS_PENDIENTES, matchMode: 'in' } }
    if (this.busqueda.trim()) filtros['q'] = { value: this.busqueda.trim(), matchMode: 'contains' }

    const request = ++this.latestCount
    this.api.getAll(1, 1, filtros).subscribe((r: any) => {
      if (request !== this.latestCount) return
      this.pendientesTotal = r?.error ? null : (r?.data?.totalItems ?? 0)
    })
  }

  private aFila(p: any): FilaPago {
    const monto = p?.amount ?? 0
    //* Si el backend no manda lo del libro —una versión anterior—, se cae a la cuenta de siempre en
    //* vez de enseñar ceros. Es peor dato, pero es dato.
    const aplicado = p?.appliedLedger ?? Math.max(0, monto - (p?.pendingToApply ?? 0))
    const sinAplicar = p?.pendingLedger ?? Math.max(0, monto - aplicado)

    return {
      _id: String(p?._id ?? ''),
      fecha: p?.date ?? null,
      clienteId: p?.client?._id ? String(p.client._id) : null,
      cliente: p?.client?.name ?? '—',
      claveRastreo: p?.trackingKey ?? '—',
      monto,
      aplicado,
      sinAplicar,
      desfase: p?.ledgerDrift ?? 0,
      estado: this.estadoDe(p, monto, aplicado, sinAplicar),
      detalle: null,
      cargandoDetalle: false,
    }
  }

  /**
   * Si tiene sentido ofrecer «Anular» en esta fila.
   *
   * Mientras al pago le quede dinero sin repartir. Sobre un pago ya aplicado por completo, anular es
   * casi siempre el movimiento equivocado: **no cancela sus aplicaciones** —el arrendamiento las
   * sigue viendo— y el motor responde con `HAS_APPLICATIONS` bloqueante. Si de verdad hay que
   * deshacer algo ahí, el camino es «Cancelar aplicación» en el detalle del pago.
   *
   * El saldo se mide contra el LIBRO (`sinAplicar`), no contra `pendingToApply`: un pago atorado
   * tiene el campo en cero y el libro dice que le sobran cuarenta mil — y ese sí se puede anular si
   * resulta ser la copia repetida.
   */
  sePuedeAnular(fila: FilaPago): boolean {
    return fila.sinAplicar > 0.011
  }

  //* Sin desfase entre lo guardado y el libro: con desfase el motor repartiría otra cifra que la que
  //* enseña la fila, y eso se sanea en Por atender, no aplicando encima.
  sePuedeAplicar(fila: FilaPago): boolean {
    return fila.sinAplicar > 0.011 && fila.estado !== 'atorado' && Math.abs(fila.desfase || 0) <= 0.011 && !!fila.clienteId
  }

  /**
   * Si tiene sentido ofrecer «Cancelar aplicación» en este renglón del detalle.
   *
   * El contexto del pago ya trae solo las vivas, pero se comprueba igual: una cancelada que se
   * colara ofrecería un botón que el motor rechazaría. Sin `_id` no hay a qué apuntar.
   */
  canCancelApplication(application: any): boolean {
    return !!application?._id && application?.status !== 'CANCELLED'
  }

  /** Traduce el renglón del contexto (`letras`, `monto`) a lo que pide el diálogo de cancelar. */
  requestCancelApplication(row: FilaPago, application: any): void {
    this.cancelApplication.emit({
      paymentId: row._id,
      trackingKey: row.claveRastreo,
      applicationId: String(application._id),
      letters: application?.letras ?? [],
      amount: Number(application?.monto) || 0,
    })
  }

  /**
   * El estado del pago **según el libro**, no según su campo `status`.
   *
   * «Atorado» es un estado imposible: el pago dice «no me han aplicado» y a la vez «no me queda nada
   * por aplicar», así que el selector de aplicación no lo ofrece y ese dinero no se puede usar.
   *
   * La bandeja tenía un grupo para ellos y una operación que se los devolvía; ambos se retiraron
   * porque ninguna ruta de escritura de la API produce ese estado —todas mueven `status` y
   * `pendingToApply` juntos—. **Esta marca sí se queda**: no es la herramienta, es el diagnóstico.
   * Si un día vuelve a aparecer uno —una migración, un script de reparación—, esta fila lo dice en
   * vez de disfrazarlo de «sin aplicar», que sería una fila con dinero que nadie puede mover.
   */
  private estadoDe(p: any, monto: number, aplicado: number, sinAplicar: number): FilaPago['estado'] {
    if ((p?.pendingToApply ?? 0) <= 0 && p?.status !== 'APPLIED' && aplicado < monto - 0.011) return 'atorado'
    if (aplicado <= 0.011) return 'sinAplicar'
    if (sinAplicar > 0.011) return 'parcial'

    return 'aplicado'
  }
}
